import './tenant-form.css';

import { useMutation, useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { useSnackbar } from 'material-ui-snackbar-provider';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import Select from 'react-select';

const GET_PLATFORMSOURCE = gql`
  query PlatformSource {
    platforms {
      value
      label
    }
  }
`;

const FETCH_TENANTS_QUERY = gql`
  query {
    tenants {
      id
      domainName
      domainId
      channels
      concurrentUser
      concurrentSessions
      onHoldUsers
      isActive
      adminNotification
    }
  }
`;

const CREATE_TENANT_MUTATION = gql`
  mutation tenantCreate($tenantInput: tenantInput) {
    tenantCreate(tenantInput: $tenantInput) {
      id
    }
  }
`;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#84cc16' : '#e5e7eb',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(132, 204, 22, 0.12)' : 'none',
    borderRadius: 8,
    minHeight: 40,
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    '&:hover': { borderColor: '#d1d5db' },
  }),
  multiValue: (base) => ({
    ...base,
    backgroundColor: '#ecfccb',
    borderRadius: 4,
  }),
  multiValueLabel: (base) => ({
    ...base,
    color: '#4d7c0f',
    fontSize: 12,
    fontWeight: 500,
  }),
  multiValueRemove: (base) => ({
    ...base,
    color: '#4d7c0f',
    '&:hover': { backgroundColor: '#d9f99d', color: '#3f6212' },
  }),
  placeholder: (base) => ({
    ...base,
    color: '#9ca3af',
    fontSize: 14,
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 14,
    fontFamily: "'Inter', sans-serif",
    backgroundColor: state.isFocused ? '#f7fee7' : 'transparent',
    color: '#111827',
  }),
};

function AddTenant() {
  const snackbar = useSnackbar();
  const history = useHistory();

  const { data = {} } = useQuery(GET_PLATFORMSOURCE, {
    fetchPolicy: 'cache-and-network',
  });
  const [tenantCreate, { loading: submitting }] = useMutation(CREATE_TENANT_MUTATION);

  const [values, setValues] = useState({
    domainName: '',
    domainId: '',
    concurrentUser: '',
    concurrentSessions: '',
    onHoldUsers: '',
    channels: [],
    notificationText: '',
    roles: ['manager', 'agent', 'supervisor'],
    adminRol: false,
    isActive: true,
    adminNotification: true,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleChannels = (selected) => {
    setValues((prev) => ({ ...prev, channels: selected || [] }));
  };

  const handleAdminRol = () => {
    setValues((prev) => {
      const hasAdmin = prev.roles.includes('admin');
      const roles = hasAdmin
        ? prev.roles.filter((r) => r !== 'admin')
        : [...prev.roles, 'admin'];
      return { ...prev, roles, adminRol: !hasAdmin };
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    tenantCreate({
      variables: {
        tenantInput: {
          domainName: values.domainName,
          domainId: values.domainId,
          username: values.domainId,
          password: values.domainId,
          roles: values.roles,
          firstName: values.domainName,
          lastName: values.domainId,
          channels: values.channels,
          concurrentUser: parseInt(values.concurrentUser, 10),
          concurrentSessions: parseInt(values.concurrentSessions, 10),
          onHoldUsers: parseInt(values.onHoldUsers, 10),
          adminNotification: values.adminNotification,
          adminRol: values.adminRol,
          isActive: values.isActive,
          notificationText: values.notificationText,
        },
      },
      refetchQueries: [{ query: FETCH_TENANTS_QUERY }],
      awaitRefetchQueries: true,
    })
      .then((r) => {
        if (r.data.tenantCreate !== undefined) {
          snackbar.showMessage('Tenant created successfully');
          history.push('/tenants');
        }
      })
      .catch(console.error);
  };

  return (
    <div className="tenant-form-page">
      {/* Page header */}
      <div className="form-page-header">
        <div>
          <div className="form-breadcrumb">
            <a href="/tenants">Tenants</a>
            <span className="form-breadcrumb-sep">/</span>
            <span className="form-breadcrumb-current">New Tenant</span>
          </div>
          <h1 className="form-page-title">New Tenant</h1>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="form-card">
          <div className="form-card-body">
            <p className="form-section-title">Tenant Details</p>

            <div className="form-grid">
              {/* Domain Name */}
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="domainName">
                  Domain Name
                </label>
                <input
                  className="form-input"
                  id="domainName"
                  name="domainName"
                  type="text"
                  placeholder="e.g. OmniQ"
                  value={values.domainName}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Domain ID */}
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="domainId">
                  Domain ID
                </label>
                <input
                  className="form-input"
                  id="domainId"
                  name="domainId"
                  type="text"
                  placeholder="e.g. omniq-001"
                  value={values.domainId}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Concurrent Users */}
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="concurrentUser">
                  Concurrent Users
                </label>
                <input
                  className="form-input"
                  id="concurrentUser"
                  name="concurrentUser"
                  type="number"
                  min="0"
                  placeholder="e.g. 1000"
                  value={values.concurrentUser}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Concurrent Sessions */}
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="concurrentSessions">
                  Concurrent Sessions
                </label>
                <input
                  className="form-input"
                  id="concurrentSessions"
                  name="concurrentSessions"
                  type="number"
                  min="0"
                  placeholder="e.g. 250"
                  value={values.concurrentSessions}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Hold Sessions */}
              <div className="form-group">
                <label className="form-label form-label-required" htmlFor="onHoldUsers">
                  Hold Sessions
                </label>
                <input
                  className="form-input"
                  id="onHoldUsers"
                  name="onHoldUsers"
                  type="number"
                  min="0"
                  placeholder="e.g. 10"
                  value={values.onHoldUsers}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Channels */}
              <div className="form-group">
                <label className="form-label" htmlFor="channels">
                  Channels
                </label>
                <Select
                  inputId="channels"
                  isMulti
                  name="channels"
                  value={values.channels}
                  onChange={handleChannels}
                  options={data.platforms}
                  styles={selectStyles}
                  placeholder="Select channels…"
                  isClearable
                />
              </div>

              {/* Notification Text — full width */}
              <div className="form-group form-grid-full">
                <label className="form-label" htmlFor="notificationText">
                  Notification Text
                </label>
                <textarea
                  className="form-textarea"
                  id="notificationText"
                  name="notificationText"
                  placeholder="e.g. Welcome to our platform"
                  value={values.notificationText}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-divider" />

            <p className="form-section-title">Settings</p>

            {/* Is Active toggle */}
            <div className="toggle-row" style={{ marginBottom: 12 }}>
              <div className="toggle-row-label">
                <span className="toggle-label-text">Active</span>
                <span className="toggle-label-desc">
                  Tenant is enabled and accessible
                </span>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={values.isActive}
                  onChange={() =>
                    setValues((prev) => ({ ...prev, isActive: !prev.isActive }))
                  }
                />
                <span className="toggle-track" />
                <span className="toggle-thumb" />
              </label>
            </div>

            {/* Checkboxes */}
            <div className="checkbox-rows">
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={values.adminNotification}
                  onChange={() =>
                    setValues((prev) => ({
                      ...prev,
                      adminNotification: !prev.adminNotification,
                    }))
                  }
                />
                <span className="checkbox-row-label">Admin Notifications</span>
              </label>
              <label className="checkbox-row">
                <input
                  type="checkbox"
                  checked={values.adminRol}
                  onChange={handleAdminRol}
                />
                <span className="checkbox-row-label">Admin Role</span>
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="form-card-footer">
            <button
              type="button"
              className="btn-ghost"
              onClick={() => history.push('/tenants')}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary-form"
              disabled={submitting}
            >
              {submitting ? 'Creating…' : 'Create Tenant'}
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default AddTenant;
